<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Insights') }}
    </template>
    <template v-if="props.dashboards.length" #header-actions>
      <WdsSegmentedControl
        :options="options"
        :default-active-key="activeTab"
        @click="changeInsightsVersion"
      />
    </template>
  </PageHeader>

  <div
    v-for="dashboard of options"
    v-show="activeTab === dashboard.optionKey"
    :key="dashboard.optionKey"
  >
    <slot v-if="visitedTabs.has(dashboard.optionKey)" :name="slotName(dashboard.optionKey)"></slot>
  </div>
</template>

<script setup lang="ts">
import { WdsSegmentedControl } from '@worksome/wds-components'
import { useLocalStorage } from '@vueuse/core'
import { computed, reactive, watch } from 'vue'
import { $t } from '@/composables/translation'
import PageHeader from '@/components/PageHeader.vue'

type Dashboard = {
  id: string
  label: string | null
}

const DEFAULT_TAB = 'default'
const LEGACY_CUSTOM_VALUE = 'true'

const props = defineProps<{
  dashboards: Dashboard[]
}>()

const storedTab = useLocalStorage<string>('useEmbeddedInsights', DEFAULT_TAB)

const options = computed(() => [
  { optionKey: DEFAULT_TAB, title: $t('Default Insights') },
  ...props.dashboards.map((dashboard) => ({
    optionKey: dashboard.id,
    title: dashboard.label || $t('Custom Insights'),
  })),
])

const slotName = (optionKey: string) =>
  optionKey === DEFAULT_TAB ? 'old-dashboard' : `dashboard-${optionKey.toLowerCase()}`

const activeTab = computed(() => {
  if (storedTab.value === LEGACY_CUSTOM_VALUE) {
    return props.dashboards[0]?.id ?? DEFAULT_TAB
  }

  return props.dashboards.some((dashboard) => dashboard.id === storedTab.value)
    ? storedTab.value
    : DEFAULT_TAB
})

// Looker Studio sizes its report at load, so an iframe loaded while hidden renders blank.
const visitedTabs = reactive(new Set<string>())
watch(activeTab, (tab) => visitedTabs.add(tab), { immediate: true })

const changeInsightsVersion = function (optionKey: string) {
  storedTab.value = optionKey
}
</script>
